(() => {
  const PRODUCT_MAIN = 'main#MainContent';
  const BAR_SELECTOR = '#sticky-product-bar';
  const ATC_SELECTORS = [
    '.product-details .product-form-buttons .add-to-cart-button',
    '.product-details .product-form-buttons button[id^="BuyButtons-ProductSubmitButton-"]',
    '.product-form-buttons .add-to-cart-button',
    '.product-form-buttons button[id^="BuyButtons-ProductSubmitButton-"]',
  ];

  /** True once the main ATC has entered the viewport at least once. */
  let atcWasSeen = false;

  /** @type {HTMLElement | null} */
  let atcTarget = null;

  /** @type {IntersectionObserver | null} */
  let observer = null;

  /** @type {HTMLElement | null} */
  let activeBar = null;

  /** @type {HTMLElement | null} */
  let listenerMain = null;

  let windowListenersBound = false;

  function getBar() {
    const el = document.querySelector(BAR_SELECTOR);
    return el instanceof HTMLElement ? el : null;
  }

  function getProductMain() {
    const el = document.querySelector(PRODUCT_MAIN);
    return el instanceof HTMLElement ? el : null;
  }

  function findAtcTarget(root) {
    for (const sel of ATC_SELECTORS) {
      const el = root.querySelector(sel);
      if (el instanceof HTMLElement) return el;
    }
    return null;
  }

  /** ATC has any pixel visible in the viewport. */
  function isAtcInView(target) {
    const r = target.getBoundingClientRect();
    return r.top < window.innerHeight && r.bottom > 0;
  }

  /** ATC has scrolled completely above the viewport (user scrolled down past it). */
  function isAtcScrolledPast(target) {
    return target.getBoundingClientRect().bottom <= 0;
  }

  function setBarVisible(bar, show) {
    bar.classList.toggle('is-visible', show);
    bar.toggleAttribute('hidden', !show);
    bar.setAttribute('aria-hidden', show ? 'false' : 'true');
  }

  function updateBar(bar) {
    if (!atcTarget) {
      setBarVisible(bar, false);
      return;
    }

    if (isAtcInView(atcTarget)) {
      atcWasSeen = true;
      setBarVisible(bar, false);
      return;
    }

    setBarVisible(bar, atcWasSeen && isAtcScrolledPast(atcTarget));
  }

  function syncStickyBar(bar) {
    const main = getProductMain();
    if (!main) return;

    const image = bar.querySelector('.pdp-sticky-bar__image');
    const title = bar.querySelector('.pdp-sticky-bar__title');
    const priceSale = bar.querySelector('.pdp-sticky-bar__price-sale');
    const priceCompare = bar.querySelector('.pdp-sticky-bar__price-compare');
    const quickAddBtn = bar.querySelector('.quick-add-btn');
    const stickyQtyInput = bar.querySelector('.pdp-sticky-bar__quantity input[type="number"]');
    const stickyQtyComponent = bar.querySelector('.pdp-sticky-bar__quantity quantity-selector-component');

    const titleSource = main.querySelector('.product-details h1');
    const priceSaleSource = main.querySelector('[data-product-price] .product-price-final .money');
    const priceCompareSource = main.querySelector('[data-product-price] .product-price-comp-value');
    const mainQtyInput = main.querySelector('.product-form-buttons .quantity-selector input[type="number"]');
    const variantIdInput = main.querySelector('.product-details input[name="id"]');
    const imageSource =
      main.querySelector(
        '.product-information__media slideshow-slide[aria-hidden="false"] .product-media__image'
      ) || main.querySelector('.product-information__media .product-media__image');
    const atcButton = main.querySelector(ATC_SELECTORS.join(', '));

    if (image instanceof HTMLImageElement && imageSource instanceof HTMLImageElement) {
      image.src = imageSource.currentSrc || imageSource.src;
      image.alt = imageSource.alt || titleSource?.textContent?.trim() || '';
    }
    if (title instanceof HTMLElement && titleSource) {
      title.textContent = titleSource?.textContent?.trim() || '';
    }
    if (priceSale instanceof HTMLElement) {
      priceSale.textContent = priceSaleSource?.textContent?.trim() || '';
    }
    if (priceCompare instanceof HTMLElement) {
      const compareText = priceCompareSource?.textContent?.trim();
      if (compareText) {
        const base = compareText.replace(/\s*Comp\.?\s*value/i, '').trim();
        priceCompare.textContent = base ? `${base} Comp. value` : '';
        priceCompare.hidden = !priceCompare.textContent;
      } else {
        priceCompare.textContent = '';
        priceCompare.hidden = true;
      }
    }
    if (quickAddBtn instanceof HTMLButtonElement) {
      quickAddBtn.disabled = Boolean(atcButton?.disabled);
    }

    const variantId = variantIdInput instanceof HTMLInputElement ? variantIdInput.value : '';
    if (variantId) {
      const singleAddBtn = bar.querySelector('[data-single-add-to-cart]');
      if (singleAddBtn instanceof HTMLButtonElement) {
        singleAddBtn.dataset.variantId = variantId;
      }
      if (stickyQtyComponent instanceof HTMLElement) {
        stickyQtyComponent.dataset.variantId = variantId;
      }
    }

    if (stickyQtyInput instanceof HTMLInputElement && mainQtyInput instanceof HTMLInputElement) {
      stickyQtyInput.value = mainQtyInput.value;
      stickyQtyInput.min = mainQtyInput.min;
      stickyQtyInput.max = mainQtyInput.max;
      stickyQtyInput.step = mainQtyInput.step;
      stickyQtyInput.disabled = mainQtyInput.disabled;
    }
  }

  function watchAtc(bar) {
    const main = getProductMain();
    if (!main) return;

    const target = findAtcTarget(main);
    if (!target) return;

    atcTarget = target;
    observer?.disconnect();
    atcWasSeen = false;
    setBarVisible(bar, false);

    observer = new IntersectionObserver(() => updateBar(bar), {
      threshold: [0, 0.01, 0.5, 1],
    });
    observer.observe(target);
    updateBar(bar);
  }

  function bindWindowListeners(bar) {
    if (windowListenersBound) return;
    windowListenersBound = true;
    window.addEventListener('scroll', () => updateBar(bar), { passive: true });
    window.addEventListener('resize', () => updateBar(bar), { passive: true });
  }

  function resetState() {
    observer?.disconnect();
    observer = null;
    atcWasSeen = false;
    atcTarget = null;
    activeBar = null;
  }

  function init() {
    const bar = getBar();
    if (!bar) return;

    activeBar = bar;
    setBarVisible(bar, false);

    const main = getProductMain();
    if (!main) return;

    const refresh = () => {
      syncStickyBar(bar);
      watchAtc(bar);
    };

    bindWindowListeners(bar);

    if (main !== listenerMain) {
      listenerMain = main;
      main.addEventListener('variant:update', () => window.setTimeout(refresh, 0));
      main.addEventListener('slideshow:select', () => syncStickyBar(bar));
    }

    refresh();
  }

  function boot() {
    resetState();
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
